import Group from 'components/utils/Group'
import TextButton from 'LuxKit/Button/TextButton'
import TextLink from 'LuxKit/TextLink'
import LineExternalLinkAltIcon from 'LuxKit/Icons/line/LineExternalLinkAltIcon'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import { useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import CurrencyContext from 'contexts/currencyContext'
import { useAppSelector } from 'hooks/reduxHooks'
import { getCanViewLuxPlusBenefits, getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import { getOfferValueDisplayVisibility } from 'lib/offer/getOfferDisplayPricing'
import PriceDetails from 'tripPlanner/components/ItemOfferPrice/PriceDetails'
import type { DrawerHotelPrice, DrawerHotelUnavailableReason } from './types'
import useWarnMissingCurrency from 'tripPlanner/pages/chat/ChatTripPlannerPage/hooks/useWarnMissingCurrency'
import { getAccommodationUnavailableLabel } from 'tripPlanner/utils/accommodationPricingDisplay'

interface Props {
  price?: DrawerHotelPrice
  luxPlusPrice?: number
  value?: number
  memberValue?: number
  discountPercent?: number
  memberDiscountPercent?: number
  isTotal?: boolean
  includesTaxesAndFees?: boolean
  unavailableReason?: DrawerHotelUnavailableReason
  offerId: string
  deeplinkUrl?: string
  saleUnit?: string
  isBooked?: boolean
  orderId?: string
}

function HotelDetailPricing({
  price,
  luxPlusPrice,
  value,
  memberValue,
  discountPercent,
  memberDiscountPercent,
  isTotal,
  includesTaxesAndFees,
  unavailableReason,
  offerId,
  deeplinkUrl,
  saleUnit,
  isBooked,
  orderId,
}: Props) {
  const intl = useIntl()
  const currentCurrency = useContext(CurrencyContext)
  const canViewLuxPlusBenefits = useAppSelector(getCanViewLuxPlusBenefits)
  const luxPlusEnabled = useAppSelector(getIsLuxPlusEnabled)
  const showLuxPlusPricing = luxPlusEnabled && !!luxPlusPrice && !!price && luxPlusPrice < price.total
  const bookNowHref = deeplinkUrl || `/offer/${offerId}`

  const missingCurrencyContext = useMemo(() => ({ offerId }), [offerId])
  useWarnMissingCurrency(
    Boolean(price && !price.currency),
    '[TripChat] Hotel detail pricing missing currency',
    missingCurrencyContext,
  )

  const bookingDetailsLabel = intl.formatMessage({
    id: 'V3if7yCh9Y75j9TcYdbF',
    defaultMessage: 'Booking details',
    description: 'CTA button text to view booking details in trip planner',
  })

  if (isBooked) {
    return (
      <Group direction="horizontal" horizontalAlign="end">
        <TextButton
          kind="tertiary"
          size="large"
          href={orderId ? `/account/my-escapes/order/${orderId}` : '/account/my-escapes'}
          target="_blank"
          rel="noopener noreferrer"
        >
          {bookingDetailsLabel}
        </TextButton>
      </Group>
    )
  }

  if (!price && unavailableReason) {
    return (
      <BodyText variant="large" weight="bold">
        {getAccommodationUnavailableLabel(unavailableReason, intl)}
      </BodyText>
    )
  }

  if (!price) {
    return (
      <Group direction="vertical" gap={16}>
        <Heading variant="heading4">
          {intl.formatMessage({
            id: 'haM13lTwzMAV8P10PNiJ',
            defaultMessage: 'Pricing',
            description: 'Heading for hotel pricing section on the hotel or room booking page',
          })}
        </Heading>
        <Group
          direction="vertical"
          tabletDirection="horizontal"
          gap={16}
          tabletHorizontalAlign="space-between"
          tabletVerticalAlign="center"
        >
          <TextLink
            size="medium"
            href={bookNowHref}
            target="_blank"
            rel="noopener noreferrer"
            endIcon={<LineExternalLinkAltIcon />}
          >
            {intl.formatMessage({
              id: 'wBc1bOKcrj5AocujDj03',
              defaultMessage: 'View on Luxury Escapes for pricing details',
              description: 'Link to view hotel pricing on Luxury Escapes website',
            })}
          </TextLink>
        </Group>
      </Group>
    )
  }

  const priceLabel = isTotal
    ? intl.formatMessage(
        {
          id: 'qJsLbdcZF_W4ZSzCFkgd',
          defaultMessage: '{count, plural, one {# night} other {# nights}} total',
          description: 'Hotel pricing caption showing a selected-rate total in the trip planner AI chat',
        },
        { count: price.nights },
      )
    : intl.formatMessage(
        {
          id: 'TmpthJ75SbMB0p3uodV5',
          defaultMessage: '{count, plural, one {# night} other {# nights}} from',
          description: 'Hotel pricing caption showing number of nights in the trip planner AI chat',
        },
        { count: price.nights },
      )
  const isMemberDiscountShown = showLuxPlusPricing && canViewLuxPlusBenefits && memberDiscountPercent != null
  const effectiveDiscountPercent = isMemberDiscountShown ? memberDiscountPercent : discountPercent
  // TRP-4392: value must come from the SAME basis as the discount being shown, or the
  // strikethrough RRP, the price and the percentage stop reconciling for a member viewer.
  // memberValue falls back to the standard value when the source has no distinct member
  // value leg — mirrors the backend's own fallback (priceOption.ts).
  const effectiveValue = isMemberDiscountShown ? (memberValue ?? value) : value
  const { shouldShowDiscount } = getOfferValueDisplayVisibility({
    value: effectiveValue,
    discountPercent: effectiveDiscountPercent,
    shouldDisplayValue: true,
  })

  return (
    <PriceDetails
      price={price.total}
      luxPlusPrice={luxPlusPrice}
      currencyCode={price.currency ?? currentCurrency}
      priceLabel={priceLabel}
      totalLabel={isTotal ? 'total' : saleUnit ?? 'total'}
      value={shouldShowDiscount ? effectiveValue : undefined}
      discount={shouldShowDiscount ? effectiveDiscountPercent : undefined}
      discountDisplayType="following"
      showTaxesAndFees={includesTaxesAndFees ?? false}
    />
  )
}

export default HotelDetailPricing
